import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';
import { OrderDetailAttributes } from '@albatrosdeveloper/ave-models-package';
import { getCode } from 'app/utils/method';

/**
 * Material Cell Renderer Component
 *
 * Reusable cell renderer for displaying material information.
 * Shows material code (bold) on first line and material name on second line.
 * Uses the getCode utility function to extract the code from externalKeys or code field.
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'materialName',
 *   cellRenderer: MaterialCellRendererComponent
 * }
 * ```
 */
@Component({
  selector: "app-material-cell-renderer",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="text-content">
      <b class="f-10" *ngIf="materialCode"> {{ materialCode }} <br /> </b>
      <span class="font-descrip">{{ materialName }}</span>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialCellRendererComponent
  implements CellRenderer<OrderDetailAttributes, any>
{
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, any>;

  /**
   * Get material code using getCode utility function
   */
  get materialCode(): string | null {
    return this.params?.data?.materialName?.code ?? this.params?.data?.material?.code;
  }

  /**
   * Get material name from row data
   */
  get materialName(): string {
    return this.params?.data?.materialName?.name ?? this.params?.data?.material?.name ?? '-';
  }
}
